<template>
	<div class="history-tab">
		<div class="filter-bar">
			<div class="filter-group">
				<v-select
					v-model="filterScope"
					:items="FILTER_OPTIONS"
					:label="$t('planning_show_history')"
					density="compact"
					variant="outlined"
					hide-details
					class="filter-select"
					:menu-props="{ contentClass: 'history-select-menu' }"
				/>
			</div>
		</div>

		<v-divider />
		<div v-if="!isLoading && !hasVisibleHistory" class="empty-state">
			<v-icon size="32" color="#bdbdbd">mdi-history</v-icon>
			<span class="empty-state-text">{{ t('no_history_available') }}</span>
		</div>
		<div class="timeline-scroll" :class="{ 'is-loading': isLoading }">
			<div v-if="isLoading" class="loading-overlay">
				<v-progress-circular indeterminate size="32" width="3" color="primary" />
			</div>
			<div class="timeline">
				<template v-for="(entry, idx) in currentHistory" :key="`${entry.id}`">
					<div v-if="formatDutyMessage(entry)" class="timeline-entry">
						<div class="timeline-icon">
							<v-icon size="16" color="#9e9e9e">mdi-history</v-icon>
						</div>

						<div class="entry-content">
							<v-row>
								<v-col cols="2">
									<div class="entry-meta">
										<div class="entry-date">{{ formatDate(entry.createdAt) }}</div>
										<div class="entry-time">{{ formatTime(entry.createdAt) }}</div>
									</div>
								</v-col>
								<v-col>
									<div class="entry-title">{{ formatDutyMessage(entry) }}</div>

									<div class="changes-list">
										<div class="change-row">
											<span class="change-val old">{{ changeValOld(entry) }}</span>
											<v-icon size="14" color="#000000" class="arrow-icon">mdi-arrow-right</v-icon>
											<span class="change-val new">{{ changeValNew(entry) }}</span>
										</div>
									</div>
								</v-col>
							</v-row>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHistoryTab } from '@/rennovation/planning-view/composables/useHistoryTab';
import { useHistoryFormatter } from '@/rennovation/planning-view/composables/useHistoryFormatter';
import { useDateTime } from '../../../../../../components/composables/useDateTime';

const { t } = useI18n();

// --- Props ---
interface Props {
	dutyID?: string;
	userID?: string;
	dayID?: string;
}

const props = defineProps<Props>();

defineOptions({
	inheritAttrs: false,
});

const FILTER_OPTIONS = [
	{ title: t('planning_duty'), value: 'duty' },
	{ title: t('planning_user'), value: 'user' },
] as const;

const { filterScope, isLoading, currentHistory } = useHistoryTab({
	dutyID: props.dutyID,
	userID: props.userID,
	dayID: props.dayID,
});
const { formatDate, formatTime } = useDateTime();
const { formatDutyMessage, formatUserName } = useHistoryFormatter();

function hasDutyMessage(entry) {
	const { event: eventType, updates } = entry;
	if (eventType === 'created' || eventType === 'deleted') return true;
	if (eventType === 'updated') return updates?.user !== undefined;
	return false;
}

const hasVisibleHistory = computed(() => {
	const history = currentHistory.value;
	return Array.isArray(history) && history.some((entry) => hasDutyMessage(entry));
});

function changeValOld({ event, record, updates }) {
	switch (event) {
		case 'created':
			return formatUserName(null);
		case 'deleted':
			return formatUserName(record?.lastAssignedUser);
		default:
			return formatUserName(record?.user);
	}
}

function changeValNew({ event, record, updates }) {
	switch (event) {
		case 'created':
			return formatUserName(record?.user);
		case 'deleted':
			return formatUserName(null);
		default:
			return formatUserName(updates?.user);
	}
}
</script>

<style scoped>
.history-tab {
	display: flex;
	flex-direction: column;
	max-height: 607px;
	max-width: 960px;
	min-height: 400px;
}

/* ── Filter Bar ── */
.filter-bar {
	display: flex;
	align-items: center;
	gap: 24px;
	padding: 16px 24px;
	flex-shrink: 0;
}

.filter-group {
	display: flex;
	align-items: center;
	gap: 12px;
}

.filter-label {
	font-family: Roboto, sans-serif;
	font-size: 12px;
	font-weight: 400;
	color: rgba(0, 0, 0, 0.6);
	white-space: nowrap;
}

.filter-select {
	width: 216px;
}

.filter-select :deep(.v-field) {
	border-width: 1px;
	border-color: rgba(0, 0, 0, 0.15) !important;
	height: 36px;
	border-radius: 4px;
}

/* Label color */
.filter-select :deep(.v-label) {
	color: #757575 !important;
	opacity: 1 !important;
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 20px;
	letter-spacing: 0.4px;
	vertical-align: middle;
}

/* Arrow icon color */
.filter-select :deep(.v-field__append-inner .v-icon) {
	color: #757575 !important;
	opacity: 1 !important;
}

/* Selected (input) text color */
.filter-select :deep(.v-field__input) {
	color: #000000de !important;
	padding: 8px 12px;
	gap: 10px;
}

/* ── Timeline Scroll ── */
.timeline-scroll {
	overflow-y: auto;
	flex: 1;
	padding: 8px 24px 24px;
	position: relative;
	display: flex;
	flex-direction: column;
}
.loading-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.6);
	z-index: 1;
}

.timeline-scroll.is-loading {
	overflow: hidden;
}

.timeline-scroll::-webkit-scrollbar {
	width: 4px;
}
.timeline-scroll::-webkit-scrollbar-track {
	background: transparent;
}
.timeline-scroll::-webkit-scrollbar-thumb {
	background: rgba(0, 0, 0, 0.12);
	border-radius: 4px;
}

/* ── Timeline ── */
.timeline {
	position: relative;
	padding-left: 44px;
}

/* Vertical connector line */
.timeline::before {
	content: '';
	position: absolute;
	left: 12px;
	top: 24px;
	bottom: 24px;
	width: 1px;
	background: rgba(0, 0, 0, 0.12);
}

/* ── Entry ── */
.timeline-entry {
	position: relative;
	display: flex;
	gap: 0;
	padding: 16px 0;
}

/* ── Circle Icon ── */
.timeline-icon {
	position: absolute;
	left: -44px;
	top: 18px;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background: #eeeeee;
	border: 1.5px solid rgba(0, 0, 0, 0.15);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

/* ── Entry Content ── */
.entry-content {
	flex: 1;
	min-width: 0;
}

.entry-meta {
	align-items: baseline;
	margin-bottom: 2px;
}

.entry-date {
	font-family: Roboto, sans-serif;
	font-size: 14px;
	color: rgba(0, 0, 0, 0.45);
	font-weight: 400;
}

.entry-time {
	font-family: Roboto, sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 1.5;
	letter-spacing: 0;
	color: rgba(0, 0, 0, 0.45);
	font-weight: 400;
}

.entry-title {
	font-family: 'Roboto', sans-serif;
	font-weight: 500;
	font-size: 14px;
	line-height: 1.5;
	letter-spacing: 0;
	color: rgba(0, 0, 0);
}

.action-text {
	font-weight: 400;
}

/* ── Changes ── */
.changes-list {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.change-row {
	display: flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
}

.change-val {
	font-family: 'Roboto', sans-serif;
	font-weight: 400;
	font-size: 12px;
	line-height: 1.5;
	letter-spacing: 0;
	color: rgba(0, 0, 0, 0.65);
}

/* No colored badges — plain text with arrow, matching the screenshot */
.change-val.old {
	color: #888c9d;
}

.change-val.new {
	color: #000000;
}

.arrow-icon {
	flex-shrink: 0;
}

.empty-state {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 8px;
	pointer-events: none;
}

.empty-state-text {
	font-family: Roboto, sans-serif;
	font-size: 14px;
	font-weight: 400;
	color: rgba(0, 0, 0, 0.38);
}
</style>
